import React, { useState, useEffect } from 'react';
import { 
  gethelpercategory, 
  gethelperservices, 
  addprice_to_helper_provider,
  getaddedpricevalues 
} from '../../../Apiservices/Auth/servers/fetchProviderData';
import ServicePriceCard from './ServicePriceCard';
import './HelperPriceManager.css';
import { useSelector } from 'react-redux';

const HelperPriceManager = ({  }) => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [providerPrices, setProviderPrices] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saveMessage, setSaveMessage] = useState({});
  const providerId = useSelector((state)=>state.auth.userId);
  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, [providerId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, servicesRes, pricesRes] = await Promise.all([
        gethelpercategory(),
        gethelperservices(),
        getaddedpricevalues(providerId)
      ]);

      setCategories(categoriesRes?.data?.data || []);
      setServices(servicesRes?.data?.data || []);
      
      // Convert prices array to object for easy access
      const pricesObj = {};
      (pricesRes?.data?.data || []).forEach(price => {
        pricesObj[price.helper_service_id] = price;
      });
      setProviderPrices(pricesObj);

      // Select first category by default
      if (categoriesRes?.data?.data?.length > 0) {
        setSelectedCategory(categoriesRes.data.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter services by selected category
  const filteredServices = selectedCategory 
    ? services.filter(service => service.category_id === selectedCategory)
    : services;

  // Handle price change with auto-save
  const handlePriceChange = async (serviceId, field, value) => {
    const updatedPrices = { ...providerPrices };
    
    if (!updatedPrices[serviceId]) {
      updatedPrices[serviceId] = {
        helper_service_id: serviceId,
        provider_id: providerId,
        price: '',
        offered_price: '',
        offer_valid_upto: '',
        description: '',
        is_active: true
      };
    }
    
    updatedPrices[serviceId][field] = value;
    setProviderPrices(updatedPrices);
    
    // Auto-save with debounce
    clearTimeout(saving[serviceId]);
    setSaving(prev => ({ ...prev, [serviceId]: true }));
    
    const timerId = setTimeout(async () => {
      try {
        await addprice_to_helper_provider(updatedPrices[serviceId]);
        setSaveMessage(prev => ({ 
          ...prev, 
          [serviceId]: { 
            message: 'Price saved successfully!', 
            type: 'success' 
          } 
        }));
        
        // Clear message after 3 seconds
        setTimeout(() => {
          setSaveMessage(prev => {
            const newMessages = { ...prev };
            delete newMessages[serviceId];
            return newMessages;
          });
        }, 3000);
      } catch (error) {
        setSaveMessage(prev => ({ 
          ...prev, 
          [serviceId]: { 
            message: 'Failed to save. Please try again.', 
            type: 'error' 
          } 
        }));
      } finally {
        setSaving(prev => ({ ...prev, [serviceId]: false }));
      }
    }, 1000);
    
    setSaving(prev => ({ ...prev, [serviceId]: timerId }));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading services and prices...</p>
      </div>
    );
  }

  return (
    <div className="helper-price-manager">
      {/* <div className="manager-header">
        <h2>Manage Service Prices</h2>
        <p className="provider-id">Provider ID: {providerId}</p>
      </div> */}
      
      <div className="manager-layout">
        {/* Left Side - Categories */}
        <div className="categories-section">
          <h3 className="section-title">Categories</h3>
          <div className="categories-list">
            {categories.map(category => (
              <div
                key={category.id}
                className={`category-card ${selectedCategory === category.id ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <div className="category-image-container">
                  {category.image_url ? (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="category-image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="category-image-placeholder">
                      {category.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="category-info">
                  <h4 className="category-name">{category.name}</h4>
                  <p className="category-services">
                    {services.filter(s => s.category_id === category.id).length} services
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Services */}
        <div className="services-section">
          <div className="services-header">
            <h3 className="section-title">
              {selectedCategory 
                ? categories.find(c => c.id === selectedCategory)?.name + ' Services'
                : 'All Services'
              }
            </h3>
            <p className="services-count">
              {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''} available
            </p>
          </div>
          
          {filteredServices.length > 0 ? (
            <div className="services-grid">
              {filteredServices.map(service => (
                <ServicePriceCard
                  key={service.id}
                  service={service}
                  priceData={providerPrices[service.id]}
                  onPriceChange={handlePriceChange}
                  isSaving={saving[service.id]}
                  saveMessage={saveMessage[service.id]}
                />
              ))}
            </div>
          ) : (
            <div className="no-services">
              <p>No services available for this category.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelperPriceManager;